import React, { useState, useEffect, useRef, useContext } from "react";
import { API, AuthContext } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Building2, Loader2, Upload, CheckCircle2, ImageIcon } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { key: "name", label: "Company name", placeholder: "e.g. DCS Built", required: true },
  { key: "abn", label: "ABN", placeholder: "12 345 678 901" },
  { key: "licence_number", label: "Builder / contractor licence no.", placeholder: "e.g. BLD123456" },
  { key: "contact_email", label: "Contact email", placeholder: "office@yourcompany.com.au", type: "email" },
  { key: "contact_phone", label: "Contact phone", placeholder: "0400 000 000" },
  { key: "website", label: "Website", placeholder: "https://yourcompany.com.au" },
];

export default function CompanyProfile() {
  const { user } = useContext(AuthContext);
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [noAccount, setNoAccount] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [form, setForm] = useState({
    name: "", abn: "", licence_number: "", contact_email: "",
    contact_phone: "", website: "", business_address: "",
  });

  const token = () => localStorage.getItem("token");

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/account/profile`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const data = await res.json();
      if (!data.account) { setNoAccount(true); return; }
      const a = data.account;
      setForm({
        name: a.name || "", abn: a.abn || "", licence_number: a.licence_number || "",
        contact_email: a.contact_email || "", contact_phone: a.contact_phone || "",
        website: a.website || "", business_address: a.business_address || "",
      });
      setCompleted(!!a.profile_completed);
      setLogoUrl(a.logo_url ? `${API.replace(/\/api$/, "")}${a.logo_url}?v=${Date.now()}` : null);
    } catch (e) {
      toast.error("Could not load company profile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Company name is required"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/account/profile`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token()}`, "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || "Save failed"); }
      const data = await res.json();
      setCompleted(!!data.account?.profile_completed);
      toast.success("Company profile saved");
    } catch (e) {
      toast.error(e.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const onLogoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be 2MB or smaller"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/account/logo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token()}` },
        body: fd,
      });
      if (!res.ok) { const er = await res.json(); throw new Error(er.detail || "Upload failed"); }
      const data = await res.json();
      setLogoUrl(`${API.replace(/\/api$/, "")}${data.logo_url}?v=${Date.now()}`);
      toast.success("Logo updated");
    } catch (er) {
      toast.error(er.message || "Could not upload logo");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>;
  }

  if (noAccount) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center" data-testid="company-profile-no-account">
        <Building2 className="w-10 h-10 mx-auto text-slate-300" />
        <h1 className="mt-4 text-2xl font-bold text-slate-800">Company Profile</h1>
        <p className="mt-2 text-slate-500">No company account is associated with this login. This screen is for company owners.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="company-profile-page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-amber-500 flex items-center justify-center shadow-sm">
            <Building2 className="w-6 h-6 text-slate-900" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Company Profile</h1>
            <p className="text-slate-500 text-sm">Your details shown on client documents and communications.</p>
          </div>
        </div>
        {completed ? (
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 gap-1" data-testid="profile-complete-badge">
            <CheckCircle2 className="w-3.5 h-3.5" /> Details complete
          </Badge>
        ) : (
          <Badge variant="outline" className="text-amber-700 border-amber-300" data-testid="profile-incomplete-badge">Incomplete</Badge>
        )}
      </div>

      <Card className="border-amber-100 shadow-sm">
        <CardContent className="p-6">
          <div className="grid md:grid-cols-[200px_1fr] gap-8">
            {/* Logo */}
            <div>
              <Label className="text-sm font-medium text-slate-700">Company logo</Label>
              <div className="mt-2 w-44 h-44 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                {logoUrl ? (
                  <img src={logoUrl} alt="Company logo" className="w-full h-full object-contain p-2" data-testid="company-logo-preview" />
                ) : (
                  <div className="text-center text-slate-400">
                    <ImageIcon className="w-8 h-8 mx-auto" />
                    <p className="text-xs mt-1">No logo yet</p>
                  </div>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onLogoPick} data-testid="company-logo-input" />
              <Button variant="outline" size="sm" className="mt-3 w-44 border-amber-200" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-logo-btn">
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                {logoUrl ? "Replace logo" : "Upload logo"}
              </Button>
              <p className="text-xs text-slate-400 mt-2 w-44">PNG, JPG or WEBP · max 2MB</p>
            </div>

            {/* Fields */}
            <div className="space-y-4">
              {FIELDS.map((f) => (
                <div key={f.key}>
                  <Label htmlFor={f.key} className="text-sm font-medium text-slate-700">
                    {f.label}{f.required && <span className="text-amber-600"> *</span>}
                  </Label>
                  <Input
                    id={f.key}
                    type={f.type || "text"}
                    value={form[f.key]}
                    placeholder={f.placeholder}
                    onChange={(e) => onChange(f.key, e.target.value)}
                    className="mt-1 border-slate-200 focus:border-amber-400"
                    data-testid={`company-${f.key}-input`}
                  />
                </div>
              ))}
              <div>
                <Label htmlFor="business_address" className="text-sm font-medium text-slate-700">Business address</Label>
                <textarea
                  id="business_address"
                  value={form.business_address}
                  placeholder="Street, suburb, state, postcode"
                  onChange={(e) => onChange("business_address", e.target.value)}
                  rows={3}
                  className="mt-1 w-full rounded-md border border-slate-200 focus:border-amber-400 focus:outline-none px-3 py-2 text-sm"
                  data-testid="company-business_address-input"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-slate-100">
            <Button variant="outline" onClick={load} disabled={saving} data-testid="company-cancel-btn">Reset</Button>
            <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold" onClick={save} disabled={saving} data-testid="company-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save changes
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
